import { Accordion, AccordionItem, Modal } from "@appkit4/react-components";
import React, { useEffect, useMemo, useState } from "react";
import DataImportModal from "../../components/DataImportModal/DataImportModal";
import toast from "../../components/DismissibleToast";
import {
  Button,
  Input,
  InputNumber,
} from "../../components/ReadonlyAwareInputs";
import { getPageTooltips } from "../../services/GroupContext";
import {
  setANGIETotals,
  setEBITDATotals,
  setNGIETotals,
  setQNGIETotals,
} from "../../services/calculations/GroupDataCalcs";
import GroupDataTable from "./GroupDataTable";
import {
  WarningModal,
  handleCancel,
  renderPrimaryAccordionTitle,
  renderSecondaryAccordionTitle,
  renderSummary,
  sumArray,
} from "./groupDataUtil";
import "./ngie.scss";
import { useGroupDataRows } from "./utils/groupDataUtil";
import { getNgieRowConfig } from "./utils/ngieUtils";

const NGIE = ({
  setVisible,
  currentPeriod,
  setCurrentPeriod,
  save,
  isReadOnly,
  errorState: [isError, setIsError],
  isParentError,
}) => {
  const [modalCurrency, setModalCurrency] = useState("");
  const [fxRate, setFxRate] = useState(1);

  const tooltips = useMemo(() => getPageTooltips("ngieModal"), []);
  const ngieRowConfig = useMemo(() => getNgieRowConfig(tooltips), [tooltips]);
  const tables = useGroupDataRows(ngieRowConfig);

  const expenses = {
    loanRelationshipExpenses: [
      tables["loan_relationship_interest_payable"]?.state[0],
      tables["loan_relationship_ancillary_expenses"]?.state[0],
      tables["loan_relationship_losses"]?.state[0],
      tables["interest_relevant_non_lending_relationships"]?.state[0],
    ],
    derivativeContractExpenses: [
      tables["derivatives_losses"]?.state[0],
      tables["derivatives_ancillary_losses"]?.state[0],
    ],
    leaseAndFinanceExpenses: [
      tables["finance_charges_finance_lease"]?.state[0],
      tables["finance_charges_debt_factoring"]?.state[0],
      tables["finance_charges_service_concession"]?.state[0],
      tables["alternative_finance_return_payable"]?.state[0],
      tables["interest_payable_manufactured"]?.state[0],
      tables["financing_charges_debtor_repo"]?.state[0],
      tables["financing_charges_interest_payable"]?.state[0],
    ],
    otherRelevantExpenses: [
      tables["preference_shares_payable_dividends"]?.state[0],
      tables["depreciation_relevant_expense"]?.state[0],
    ],
  };

  const [totalLoanExpenses, setTotalLoanExpenses] = useState(0);
  useEffect(() => {
    setTotalLoanExpenses(sumArray(expenses?.loanRelationshipExpenses));
  }, [expenses?.loanRelationshipExpenses]);

  const [totalDerivExpenses, setTotalDerivExpenses] = useState(0);
  useEffect(() => {
    setTotalDerivExpenses(sumArray(expenses?.derivativeContractExpenses));
  }, [expenses?.derivativeContractExpenses]);

  const [totalLeaseFinanceExpenses, setTotalLeaseFinanceExpenses] = useState(0);
  useEffect(() => {
    setTotalLeaseFinanceExpenses(sumArray(expenses?.leaseAndFinanceExpenses));
  }, [expenses?.leaseAndFinanceExpenses]);

  const [totalOtherRelevantExpenses, setTotalOtherRelevantExpenses] =
    useState(0);
  useEffect(() => {
    setTotalOtherRelevantExpenses(sumArray(expenses?.otherRelevantExpenses));
  }, [expenses?.otherRelevantExpenses]);

  const income = {
    loanAmountsIncome: [
      tables["loan_relationship_interest_receivable"]?.state[0],
      tables["loan_relationship_profits"]?.state[0],
      tables["interest_receivable_non_lending_relationships"]?.state[0],
    ],
    derivContractIncome: [tables["derivatives_gains"]?.state[0]],
    financeLeaseIncome: [
      tables["finance_income_finance_lease"]?.state[0],
      tables["finance_income_debt_factoring"]?.state[0],
      tables["finance_income_service_concession"]?.state[0],
      tables["finance_receivable_alternative_finance_arrangement"]?.state[0],
      tables["interest_receivable_manufactured"]?.state[0],
      tables["finance_income_debtor_repo"]?.state[0],
      tables["finance_income_interest_receivable"]?.state[0],
    ],
    otherRelevantIncome: [
      tables["preference_shares_dividends_receivable"]?.state[0],
      tables["depreciation_relevant_income"]?.state[0],
    ],
  };

  const [totalLoanIncome, setTotalLoanIncome] = useState(0);
  useEffect(() => {
    setTotalLoanIncome(sumArray(income?.loanAmountsIncome));
  }, [income?.loanAmountsIncome]);

  const [totalDerivContractIncome, setTotalDerivContractIncome] = useState(0);
  useEffect(() => {
    setTotalDerivContractIncome(sumArray(income?.derivContractIncome));
  }, [income?.derivContractIncome]);

  const [totalFinanceLeaseIncome, setTotalFinanceLeaseIncome] = useState(0);
  useEffect(() => {
    setTotalFinanceLeaseIncome(sumArray(income?.financeLeaseIncome));
  }, [income?.financeLeaseIncome]);

  const [otherRelevantIncomeTotal, setOtherRelevantIncomeTotal] = useState(0);
  useEffect(() => {
    setOtherRelevantIncomeTotal(sumArray(income?.otherRelevantIncome));
  }, [income?.otherRelevantIncome]);

  const adjustments = {
    autoDisregardRegGroup: [
      tables["adjustments_eliminate_derivatives"]?.state[0],
      tables["adjustments_include_replacement_derivatives"]?.state[0],
    ],
    other: [
      tables["adjustments_specialist_regimes"]?.state[0],
      tables["adjustments_other"]?.state[0],
    ],
  };

  const [autoDisregardRegGroupTotal, setAutoDisregardRegGroupTotal] =
    useState(0);
  useEffect(() => {
    setAutoDisregardRegGroupTotal(sumArray(adjustments?.autoDisregardRegGroup));
  }, [adjustments?.autoDisregardRegGroup]);

  const [otherAdjustmentsTotal, setOtherAdjustmentsTotal] = useState(0);
  useEffect(() => {
    setOtherAdjustmentsTotal(sumArray(adjustments?.other));
  }, [adjustments?.other]);

  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalAdjustments, setTotalAdjustments] = useState(0);

  const hasInterestAllowanceElection =
    currentPeriod?.interest_allowance_non_consolidated_election || false;

  const [primaryActiveKeys, setPrimaryActiveKeys] = useState([]);
  const [expenseActiveKeys, setExpenseActiveKeys] = useState([]);
  const [incomeActiveKeys, setIncomeActiveKeys] = useState([]);
  const [adjustmentsActiveKeys, setAdjustmentsActiveKeys] = useState([]);
  const onClickAccordion = (activeKeys) => {
    if (!activeKeys?.includes(1)) setExpenseActiveKeys([]);
    if (!activeKeys?.includes(2)) setIncomeActiveKeys([]);
    if (!activeKeys?.includes(3)) setAdjustmentsActiveKeys([]);
    setPrimaryActiveKeys(activeKeys);
  };
  const onClickExpenseAccordion = (activeKeys) => {
    setExpenseActiveKeys(activeKeys);
  };
  const onClickIncomeAccordion = (activeKeys) => {
    setIncomeActiveKeys(activeKeys);
  };
  const onClickAdjustmentsAccordion = (activeKeys) => {
    setAdjustmentsActiveKeys(activeKeys);
  };

  const updateNgieValues = (ngie) => {
    const newPeriod = {
      ...currentPeriod,
      currency: modalCurrency,
      fx_rate: fxRate,
      ngie,
    };

    setNGIETotals(newPeriod);

    if (newPeriod?.angie) {
      setANGIETotals(newPeriod);
    }
    if (newPeriod?.qngie && newPeriod?.angie_amount_before_any_adjustments) {
      setQNGIETotals(newPeriod);
    }
    if (newPeriod?.ebitda) {
      setEBITDATotals(newPeriod);
    }

    return newPeriod;
  };

  const submit = () => {
    const ngie = Object.keys(tables)?.reduce((acc, k) => {
      acc[k] = tables[k]?.state[0];
      return acc;
    }, {});

    const newPeriod = updateNgieValues(ngie);

    setCurrentPeriod(newPeriod);
    const promise = save(newPeriod);
    toast.promise(promise, {
      loading: "Saving NGIE data...",
      success: () => {
        setVisible(false);
        return "Saved NGIE data";
      },
      error: "Something went wrong with saving NGIE data",
    });
  };

  /*useEffect(() => {
    setIsError( Object.keys(tables)?.some( k => tables[k]?.error[0]) )
  }, [Object.keys(tables)?.map(k => tables[k]?.error[0])])*/

  useEffect(() => {
    const hasError = Object.values(tables).some((table) => table?.error[0]);
    setIsError(hasError);
  });

  useEffect(() => {
    let total =
      totalLoanExpenses +
      totalDerivExpenses +
      totalLeaseFinanceExpenses +
      totalOtherRelevantExpenses;
    setTotalExpenses(total);
  }, [
    totalLoanExpenses,
    totalDerivExpenses,
    totalLeaseFinanceExpenses,
    totalOtherRelevantExpenses,
  ]);

  useEffect(() => {
    let total =
      totalLoanIncome +
      totalDerivContractIncome +
      totalFinanceLeaseIncome +
      otherRelevantIncomeTotal;
    setTotalIncome(total);
  }, [
    totalLoanIncome,
    totalDerivContractIncome,
    totalFinanceLeaseIncome,
    otherRelevantIncomeTotal,
  ]);

  useEffect(() => {
    const total =
      autoDisregardRegGroupTotal +
      otherAdjustmentsTotal +
      sumArray([
        tables["adjustments_exclude_relevant_income"]?.state[0],
        tables["excludePartnershipIncomeAdjustment"]?.state[0],
        tables["taxAdjPBT"]?.state[0],
      ]);
    setTotalAdjustments(total);
  }, [
    autoDisregardRegGroupTotal,
    otherAdjustmentsTotal,
    tables["adjustments_exclude_relevant_income"]?.state[0],
    tables["excludePartnershipIncomeAdjustment"]?.state[0],
    tables["taxAdjPBT"]?.state[0],
  ]);

  useEffect(() => {
    const ngie = currentPeriod?.ngie;
    if (!ngie) {
      return;
    }
    setModalCurrency(currentPeriod?.currency ? currentPeriod?.currency : "GBP");
    setFxRate(
      currentPeriod?.fx_rate && currentPeriod?.currency !== "GBP"
        ? currentPeriod?.fx_rate
        : 1,
    );

    Object.keys(tables)?.forEach((key) => {
      tables[key]?.state[1](ngie[key]);
    });
  }, []);

  const [isModalChanged, setIsModalChanged] = useState(false);

  useEffect(() => {
    const currTotal = totalIncome + totalExpenses + totalAdjustments;
    if (
      currTotal > 0 &&
      currentPeriod?.local_curr_net_group_interest_expense !== currTotal
    ) {
      setIsModalChanged(true);
    } else {
      setIsModalChanged(false);
    }
  }, [totalIncome, totalExpenses, totalAdjustments]);

  const calculateNetGroupInterestExpense = () => {
    return totalIncome + totalExpenses + totalAdjustments;
  };

  const [showWarningModal, setShowWarningModal] = useState(false);

  const onImportSuccess = (ngie) => {
    const isPlainObject = (val) =>
      typeof val === "object" && val !== null && val.constructor === Object;

    if (isPlainObject(ngie)) {
      updateNgieValues(ngie);
      Object.entries(tables)?.forEach(([key, row]) => {
        row.state[1](ngie[key]);
      });
    } else {
      console.error("error setting values");
      toast.error("Error Importing NGIE");
    }
  };

  const handleExitWithoutSaving = () => {
    setIsError(false);
    setVisible(false);
  };

  return (
    <>
      <Modal
        visible={true}
        title={`Net Group Interest Expense (Income)`}
        onCancel={() =>
          handleCancel(
            isModalChanged,
            isReadOnly,
            setVisible,
            setShowWarningModal,
            setIsError,
          )
        }
        maskCloseable={false}
        modalStyle={{ width: "90%", maxHeight: "90%" }}
        bodyStyle={{ maxHeight: "90%" }}
      >
        <table className="table">
          <tbody>
            <tr>
              <td>
                <div className="row">
                  <p className="col-1">Select input value currency:</p>
                  <Input
                    className="col-3"
                    value={modalCurrency}
                    title={"eg. EUR, USD"}
                    onChange={(value) => {
                      setModalCurrency(value);
                      if (value === "GBP") {
                        setFxRate(1);
                      }
                    }}
                  />
                </div>
              </td>
              <td>
                <div className="row">
                  <p className="col-4">
                    Enter FX rate: 1 GBP = X Local Currency (if required):
                  </p>
                  <InputNumber
                    className="col"
                    value={modalCurrency === "GBP" ? 1 : fxRate}
                    disabled={modalCurrency === "GBP"}
                    min={0}
                    onChange={(value) => setFxRate(value)}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="row group-data-modal-top-input-header">
          <p>
            Please note, any changes to the currency or exchange rate will also
            impact the other group values
          </p>
        </div>

        {isError && (
          <div className="err">
            Description missing - a description must be provided for every
            value.
          </div>
        )}

        <Accordion
          multiple={false}
          onClick={onClickAccordion}
          activeKeys={primaryActiveKeys}
        >
          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Relevant expense amounts recognised in the group's P&L (s.411) (enter as positive numbers)",
              totalExpenses,
              tooltips.relevantExpenseAmounts,
            )}
            itemKey="1"
            style={{ border: "shading" }}
          >
            <Accordion
              multiple={true}
              onClick={onClickExpenseAccordion}
              activeKeys={expenseActiveKeys}
            >
              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Loan Relationship Amounts",
                  totalLoanExpenses,
                  tooltips.loanRelationshipAmounts,
                )}
                itemKey="4"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "loan_relationship_interest_payable",
                  "loan_relationship_ancillary_expenses",
                  "loan_relationship_losses",
                  "interest_relevant_non_lending_relationships",
                ])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Derivative Contract Amounts",
                  totalDerivExpenses,
                  tooltips.derivativeContractAmounts,
                )}
                itemKey="5"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "derivatives_losses",
                  "derivatives_ancillary_losses",
                ])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Finance Leases and Other Financing Arrangements",
                  totalLeaseFinanceExpenses,
                  tooltips.financeLeasesAndOther,
                )}
                itemKey="6"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "finance_charges_finance_lease",
                  "finance_charges_debt_factoring",
                  "finance_charges_service_concession",
                  "alternative_finance_return_payable",
                  "interest_payable_manufactured",
                  "financing_charges_debtor_repo",
                  "financing_charges_interest_payable",
                ])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Other Relevant Expense Amounts",
                  totalOtherRelevantExpenses,
                  tooltips.otherRelevantExpenseAmounts,
                )}
                itemKey="7"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "preference_shares_payable_dividends",
                  "depreciation_relevant_expense",
                ])}
              </AccordionItem>
            </Accordion>
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Relevant income amounts recognised in the group's P&L (s.411) (enter as negative numbers)",
              totalIncome,
              tooltips.relevantIncomeAmounts,
            )}
            itemKey="2"
          >
            <Accordion
              multiple={true}
              onClick={onClickIncomeAccordion}
              activeKeys={incomeActiveKeys}
            >
              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Loan Relationship Amounts",
                  totalLoanIncome,
                  tooltips.loanRelationshipAmountsIncome,
                )}
                itemKey="8"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "loan_relationship_interest_receivable",
                  "loan_relationship_profits",
                  "interest_receivable_non_lending_relationships",
                ])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Derivative Contract Amounts",
                  totalDerivContractIncome,
                  tooltips.derivativeContractAmountsIncome,
                )}
                itemKey="9"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, ["derivatives_gains"])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Finance Leases and Other Financing Arrangements",
                  totalFinanceLeaseIncome,
                  tooltips.financeLeasesAndOtherIncome,
                )}
                itemKey="10"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "finance_income_finance_lease",
                  "finance_income_debt_factoring",
                  "finance_income_service_concession",
                  "finance_receivable_alternative_finance_arrangement",
                  "interest_receivable_manufactured",
                  "finance_income_debtor_repo",
                  "finance_income_interest_receivable",
                ])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Other Relevant Income Amounts",
                  otherRelevantIncomeTotal,
                  tooltips.otherRelevantIncomeAmounts,
                )}
                itemKey="11"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "preference_shares_dividends_receivable",
                  "depreciation_relevant_income",
                ])}
              </AccordionItem>
            </Accordion>
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Other adjustments required to calculate NGIE",
              totalAdjustments,
              tooltips.otherAdjustmentsNGIE,
            )}
            itemKey="3"
          >
            <Accordion
              multiple={true}
              onClick={onClickAdjustmentsAccordion}
              activeKeys={adjustmentsActiveKeys}
            >
              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Automatic Application of Disregard Regulations at a Group Level",
                  autoDisregardRegGroupTotal,
                  tooltips.automaticDisregardRegulations,
                )}
                itemKey="12"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "adjustments_eliminate_derivatives",
                  "adjustments_include_replacement_derivatives",
                ])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Adjustments for the Interest Allowance (Non-Consolidated Investment) Election",
                  sumArray([
                    tables["adjustments_exclude_relevant_income"]?.state[0],
                  ]),
                  tooltips.adjustmentsNonConsolidated,
                )}
                itemKey="13"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "adjustments_exclude_relevant_income",
                ])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Adjustments for the Interest Allowance (Consolidated Partnership) Election",
                  sumArray([
                    tables["excludePartnershipIncomeAdjustment"]?.state[0],
                  ]),
                  tooltips.adjustmentsConsolidated,
                )}
                itemKey="14"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, ["excludePartnershipIncomeAdjustment"])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Adjustments in respect of the interest allowance (alternative calculation) election (s423-426)",
                  sumArray([tables["taxAdjPBT"]?.state[0]]),
                  tooltips.adjustmentsAlternative,
                )}
                itemKey="15"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, ["taxAdjPBT"])}
              </AccordionItem>

              <AccordionItem
                title={renderSecondaryAccordionTitle(
                  "Other",
                  otherAdjustmentsTotal,
                  tooltips.adjustmentsOther,
                )}
                itemKey="16"
                style={{ backgroundColor: "#ffffff" }}
              >
                {GroupDataTable(tables, [
                  "adjustments_specialist_regimes",
                  "adjustments_other",
                ])}
              </AccordionItem>
            </Accordion>
          </AccordionItem>
        </Accordion>

        <h3>Summary</h3>
        {renderSummary(
          modalCurrency,
          fxRate,
          calculateNetGroupInterestExpense(),
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <DataImportModal pageName="ngie" onSuccess={onImportSuccess} />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              paddingTop: "30px",
              gap: 10,
            }}
          >
            <Button
              neverReadonly
              onClick={() => {
                handleCancel(
                  isModalChanged,
                  isReadOnly,
                  setVisible,
                  setShowWarningModal,
                  setIsError,
                );
              }}
              kind="secondary"
            >
              Cancel
            </Button>
            <Button
              hiddenInReadonly
              disabled={isError || isParentError}
              onClick={submit}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      <WarningModal
        visible={showWarningModal}
        setIsVisible={setShowWarningModal}
        callBackFunc={handleExitWithoutSaving}
      ></WarningModal>
    </>
  );
};

export default NGIE;
